import React, { useContext } from 'react';
import { just, nothing } from 'maybeasy';
import { observer } from 'mobx-react';
import { SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';
import EmptyLearningCollectionIcon from '../../../../../../Cutesies/EmptyLearningCollection';
import TemplateCard from '../SectionTypeSelector/TemplateCard';
import { BlankIcon, CuratedIcon, QuickStartIcon } from '../SectionTypeSelector/Icons';
import { LearningDesign } from '../../Types/SectionTemplates';
import { T, TranslationsContext, translation } from '../../../../../../../Translations';
import { useLearningCollection } from '../../LearningCollectionContext';

interface Props {
  onSelectBlank: () => void;
  onSelectBrowseAll: () => void;
  onSelectCurated: () => void;
  canAddBrowseAll: boolean;
  curatedSections: LearningDesign[];
  isLoadingCurated: boolean;
}

function SectionsEmptyState({
  onSelectBlank,
  onSelectBrowseAll,
  onSelectCurated,
  canAddBrowseAll,
  curatedSections,
  isLoadingCurated,
}: Props) {
  const [isButtonHovered, setIsButtonHovered] = React.useState(false);
  const [showModal, setShowModal] = React.useState(false);
  const { resourceState } = useLearningCollection();
  const ts = useContext(TranslationsContext);

  const hasCuratedSections = curatedSections.length > 0;
  const showCuratedOption = hasCuratedSections || isLoadingCurated;

  const handleSelectBlank = () => {
    setShowModal(false);
    onSelectBlank();
  };

  const handleSelectBrowseAll = () => {
    setShowModal(false);
    onSelectBrowseAll();
  };

  const handleSelectCurated = () => {
    setShowModal(false);
    onSelectCurated();
  };

  return (
    <>
      <div className="mx-auto xl:container px-6 md:px-14 mb-16">
        <button
          onClick={() => setShowModal(true)}
          className="w-full bg-card border border-border rounded-lg p-12 text-center hover:border-primary hover:shadow-md transition-all cursor-pointer"
          data-intercom-target="builder-v2-add-first-section-button"
        >
          <div className="mb-6 hidden md:block [&_svg]:h-[120px] [&_svg]:w-auto [&_svg]:mx-auto">
            <EmptyLearningCollectionIcon />
          </div>

          <h3 className="mb-2" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
            <T kind="Build your own collection" />
          </h3>

          <p className="text-muted-foreground mb-6">
            <T kind="Collections are organized into sections — use them to group courses by theme, skill area, or learning phase, and to separate required from recommended." />
          </p>

          <div
            className="px-4 py-2 bg-card text-primary border border-primary rounded inline-flex items-center gap-2 transition-all"
            style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
              backgroundColor: isButtonHovered ? 'rgba(24, 122, 144, 0.1)' : undefined,
            }}
            onMouseEnter={() => setIsButtonHovered(true)}
            onMouseLeave={() => setIsButtonHovered(false)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <T kind="Add a section" />
          </div>
        </button>
      </div>

      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4"
          style={{ zIndex: 1000 }}
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-card border border-border rounded-lg shadow-lg max-w-3xl w-full p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3
                style={{ fontWeight: 'var(--font-weight-semibold)' }}
                data-intercom-target="builder-v2-add-section-modal-title"
              >
                <T kind="Add your first section" />
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-1 hover:bg-muted rounded transition-colors"
                aria-label={translation('Close dialog')(ts)}
                data-intercom-target="builder-v2-add-section-modal-close"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <TemplateCard
                title="Blank"
                description="Start with an empty section and add specific courses"
                icon={<BlankIcon />}
                onClick={handleSelectBlank}
                disabled={nothing()}
                badge={nothing()}
                intercomTarget="builder-v2-template-card-blank"
              />

              {showCuratedOption && (
                <TemplateCard
                  title="Curated"
                  description="Browse pre-built sections based on popular themes"
                  icon={<CuratedIcon />}
                  onClick={handleSelectCurated}
                  disabled={just(!hasCuratedSections || isLoadingCurated)}
                  badge={isLoadingCurated ? just('Loading...') : nothing()}
                  intercomTarget="builder-v2-template-card-curated"
                />
              )}

              <TemplateCard
                title="Quick Start"
                description="Add the full catalog of 120+ On-Demand Courses"
                icon={<QuickStartIcon />}
                onClick={handleSelectBrowseAll}
                disabled={just(!canAddBrowseAll)}
                badge={!canAddBrowseAll ? just('Already added') : nothing()}
                intercomTarget="builder-v2-template-card-quick-start"
              />
            </div>

            {resourceState.payload.has_promotables && (
              <div className="mt-6 p-4 bg-accent/10 border border-accent/30 rounded-md">
                <div className="flex items-start gap-3">
                  <SparklesIcon className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                  <p
                    className="flex-1 text-muted-foreground"
                    style={{ fontSize: 'var(--text-sm)' }}
                  >
                    <T kind="In addition to your section(s) participants will also be able to access to personalized and promoted courses from ExecOnline." />
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default observer(SectionsEmptyState);
